import throttle from 'lodash/throttle';

import { Direction } from './types';

type Position = {
  left: string;
};

type OnDragEnd = (direction: Direction, size: number) => void;

/**
 * OK, there is something funky in here I can't get to the bottom of unbinds don't seem to work which is ridiculous,
 * we pretty much follow the same structure as ResizeAgent that works just fine (I even added more debugging to verify
 * this).
 *
 * There is a quick fix to this which is to have a key on the cursor we bind to so we can force a brand new element
 * without the previous usage baggage. This works well but I'd like to get to the bottom of why the unbinding fails
 */
export class ColumnWidthAgent {
  parentElement: HTMLElement;

  onDragEnd: OnDragEnd;

  dragCursor: HTMLElement;

  originalPosition?: Position;

  throttledOnMouseMove: (e: MouseEvent) => void;

  constructor(parentElement: HTMLElement, dragCursor: HTMLElement, onDragEnd: OnDragEnd) {
    this.parentElement = parentElement;
    this.dragCursor = dragCursor;
    this.onDragEnd = onDragEnd;

    this.throttledOnMouseMove = throttle(this.onMouseMove, 50);
    dragCursor.addEventListener('mousedown', this.onMouseDown, true);
  }

  offsetToColumn = (offset: number) => {
    const offsetWithinParent = offset - this.parentElement.offsetLeft;
    const parentWidth = this.parentElement.offsetWidth;
    const columnWidth = parentWidth / 12;

    // min 0 max 12
    return Math.round(offsetWithinParent / columnWidth);
  };

  onMouseDown = (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    this.originalPosition = {
      left: this.dragCursor.style.left,
    };

    this.dragCursor.className = `${this.dragCursor.className} column-drag-active`;
    document.body.style.cursor = 'ew-resize';
    document.addEventListener('mousemove', this.throttledOnMouseMove, true);
    document.addEventListener('mouseup', this.onMouseUp, true);
  };

  // I didn't really use this but it's interesting reference:
  // https://www.w3.org/TR/css-grid-1/#algo-find-fr-size
  onMouseMove = (e: MouseEvent) => {
    const column = this.offsetToColumn(e.pageX - this.parentElement.getBoundingClientRect().x);

    const columnGap = parseInt(this.parentElement.style.columnGap, 10);
    const parentWidth = this.parentElement.clientWidth;

    // Work out size of a single column, without gaps
    const totalGapSize = 11 * columnGap;
    const singleColumnWidth = (parentWidth - totalGapSize) / 12;

    const newOffsetWithinParent = (singleColumnWidth + columnGap) * column - columnGap;
    this.dragCursor.style.left = `${newOffsetWithinParent}px`;
  };

  onMouseUp = (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    if (!this.originalPosition) {
      return;
    }

    // update the node originalPosition, if it was legal
    const initialColumn = this.offsetToColumn(parseInt(this.originalPosition.left, 10));
    const newColumn = this.offsetToColumn(parseInt(this.dragCursor.style.left, 10));

    // reset the cursor to it's original position, any modification will mount a new one
    this.dragCursor.style.left = this.originalPosition.left;
    this.dragCursor.className = this.dragCursor.className.replace(/column-drag-active/g, '').trim();

    if (initialColumn !== newColumn) {
      const direction = newColumn > initialColumn ? Direction.Right : Direction.Left;
      this.onDragEnd(direction, Math.abs(newColumn - initialColumn));
    }
    this.unsubscribe();
  };

  unsubscribe = () => {
    document.body.style.cursor = 'auto';
    document.removeEventListener('mousemove', this.throttledOnMouseMove, true);
    document.removeEventListener('mouseup', this.onMouseUp, true);
  };

  tearDown = () => {
    this.unsubscribe();
    // @ts-expect-error
    this.throttledOnMouseMove.cancel();
  };
}
